import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {QRCodeCanvas} from "qrcode.react";

import {
    cancelStoragePurchase,
    createStoragePurchase,
    getCapsuleFiles,
    getCapsuleStats,
    getStorage,
    getStoragePackages,
    getStoragePurchaseStatus,
    setStorageModalStatus
} from "../../../store/cabinet/action";
import {secondsLeftHandler, timerHandler, tokenAmountHandler} from "../../../helpers/avatar";
import {amountHandler, noteDateHandler, storageHandler, storageSizeHandler} from "../../../helpers/capsule";

// ---------------------------------------------------------------------------
// Место под файлы продаётся пакетами «N ГБ на M месяцев» и оплачивается криптой
// через тот же шлюз, что энергия: создаём счёт, показываем реквизиты прямо в
// модалке и опрашиваем статус. Место добавляет вебхук при оплате, поэтому ждём
// статус fulfilled. Пакеты срочные: у каждого свой ends_at, по истечении квота
// уменьшается, а файлы остаются — просто нельзя загружать новые
// ---------------------------------------------------------------------------

const POLL_MS = 15000;

const CabinetCapsuleStorageModal = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const opened = useSelector(store => store.cabinet.storageModalStatus);
    const storageData = useSelector(store => store.cabinet.storageData);
    const packagesData = useSelector(store => store.cabinet.storagePackages);
    const purchase = useSelector(store => store.cabinet.storagePurchase);
    const purchaseStarted = useSelector(store => store.cabinet.storagePurchaseStarted);
    const purchaseErr = useSelector(store => store.cabinet.storagePurchaseErr);

    // select -> payment -> done
    const [step, setStep] = useState('select');
    const [packId, setPackId] = useState(null);
    const [copied, setCopied] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(0);
    // ручная проверка статуса: 'checking' — ждём ответ, 'none' — платежа ещё нет
    const [checkState, setCheckState] = useState('');

    // что именно куплено этим счётом — нужно для итогового экрана
    const grantedRef = useRef(null);

    const packages = useMemo(()=> packagesData?.packages || [], [packagesData]);
    const currency = packagesData?.currency || 'USDT';

    const storage = useMemo(()=> storageHandler(storageData?.storage, false), [storageData]);
    const plans = useMemo(()=> storageData?.plans || [], [storageData]);

    const selectedPack = useMemo(
        ()=> packages.find(item => item.id === packId) || null,
        [packages, packId]
    );

    useEffect(()=>{
        if (opened){
            setStep('select');
            setCopied(false);
            grantedRef.current = null;
            dispatch(getStoragePackages());
            dispatch(getStorage());
        }
    },[opened, dispatch])

    // первый пакет выбран по умолчанию, как только список приехал
    useEffect(()=>{
        if (packages.length && packId === null){
            setPackId(packages[0].id);
        }
    },[packages, packId])

    const primaryOption = useMemo(()=>{
        const options = purchase?.payment_options || [];
        return options.find(item => item.is_primary) || options[0] || null;
    },[purchase])

    // QR ведёт на адрес получателя — кошельки понимают такую строку
    const payAddress = primaryOption?.wallet?.address || '';

    // таймер до истечения счёта
    useEffect(()=>{
        if (step !== 'payment' || !purchase?.expires_at) return undefined;

        const tick = ()=>{ setSecondsLeft(secondsLeftHandler(purchase.expires_at)); };
        tick();

        const timer = setInterval(tick, 1000);
        return ()=>{ clearInterval(timer); };
    },[step, purchase])

    // Разбор ответа статуса — один на фоновый опрос и на кнопку «Проверить
    // оплату», иначе ручная проверка молча обновляла бы стор
    const applyStatus = useCallback((data)=>{
        if (data?.status === 'fulfilled'){
            grantedRef.current = data;
            // квота выросла — обновляем и её, и счётчики на странице капсулы
            dispatch(getStorage());
            dispatch(getCapsuleStats());
            dispatch(getCapsuleFiles());
            setCheckState('');
            setStep('done');
            return true;
        }

        // счёт закрыт, платить уже некуда — возвращаем к выбору пакета
        if (data?.status === 'cancelled' || data?.status === 'expired'){
            setCheckState('');
            setStep('select');
            return true;
        }

        return false;
    },[dispatch])

    // новый шаг — старый ответ проверки уже не про него
    useEffect(()=>{
        setCheckState('');
    },[step])

    // опрос статуса: место добавляет вебхук, страница узнаёт об этом отсюда
    useEffect(()=>{
        if (step !== 'payment') return undefined;

        const id = purchase?.storage_purchase_id;
        if (!id) return undefined;

        const timer = setInterval(()=>{
            dispatch(getStoragePurchaseStatus(id, applyStatus));
        }, POLL_MS);

        return ()=>{ clearInterval(timer); };
    },[step, purchase, dispatch, applyStatus])

    // то же самое, но по кнопке: ждать до пятнадцати секунд не обязательно
    const checkPaymentHandler = () =>{
        const id = purchase?.storage_purchase_id;
        if (!id || checkState === 'checking'){
            return;
        }

        setCheckState('checking');
        dispatch(getStoragePurchaseStatus(id, (data)=>{
            if (!applyStatus(data)){
                setCheckState('none');
            }
        }, ()=>{
            setCheckState('fail');
        }));
    };

    const closeModal = useCallback(()=>{
        dispatch(setStorageModalStatus(false));
    },[dispatch])

    const payHandler = () => {
        if (!selectedPack || purchaseStarted) return;

        dispatch(createStoragePurchase(selectedPack.id, ()=>{
            setStep('payment');
        }));
    };

    const cancelHandler = () => {
        const id = purchase?.storage_purchase_id;
        if (!id) {
            setStep('select');
            return;
        }
        dispatch(cancelStoragePurchase(id, ()=>{ setStep('select'); }, ()=>{
            // счёт уже нельзя отменить — возвращаем к выбору, ошибка покажется там
            setStep('select');
        }));
    };

    const copyHandler = (value) => {
        if (!value || !navigator?.clipboard) return;
        navigator.clipboard.writeText(value).then(()=>{
            setCopied(true);
            setTimeout(()=>{ setCopied(false); }, 1500);
        });
    };

    const errorText = purchaseErr?.errors
        ? Object.values(purchaseErr.errors).flat()[0]
        : purchaseErr?.message;

    const payPrice = Number(selectedPack?.price || 0);

    const monthsName = (months) => {
        const value = Number(months) || 0;
        const tail = value % 10;
        const hundred = value % 100;
        if (hundred > 10 && hundred < 20) return t('месяцев');
        if (tail === 1) return t('месяц');
        if (tail > 1 && tail < 5) return t('месяца');
        return t('месяцев');
    };

    const subtitle = () => {
        if (step === 'done') return t('Место добавлено — можно загружать файлы дальше');
        if (step === 'payment') return t('Оплатите счёт — место добавится автоматически');
        return t('Место выдаётся на срок: когда он кончится, файлы останутся, но новые загрузить будет нельзя');
    };

    return (
        <Popup
            open={opened}
            modal
            nested
            className="capsuleModal"
            closeOnDocumentClick
            onClose={closeModal}
        >
            <div className={`${Styles.modalContainer}`}>
                <div className={`${Styles.modalCloseBtn}`} onClick={closeModal}>
                    <div/>
                    <div/>
                </div>

                <div className={`${Styles.modalContentWrap}`}>
                    <div className={`${Styles.modalHead}`}>
                        <div className={`${Styles.modalTitle}`}>{t('Больше места')}</div>
                        <div className={`${Styles.modalNote}`}>{subtitle()}</div>
                    </div>

                    <div className={`${Styles.modalBody}`}>
                        <div className={`${Styles.statsWrap}`}>
                            <div className={`${Styles.statsItem}`}>
                                <span className={`${Styles.statsName}`}>{t('Занято')}</span>
                                <span className={`${Styles.statsValue}`}>
                                    {storageSizeHandler(storage.used)} {t('из')} {storageSizeHandler(storage.quota)}
                                </span>
                                {/* единицы у занятого и у квоты разные (2 MB из 1 GB) —
                                    пропорцию показывает полоска, а не арифметика */}
                                <span className={`${Styles.quotaBar}`}>
                                    <span className={`${Styles.quotaFill}`} style={{width: `${Math.max(storage.percent, storage.used ? 1 : 0)}%`}}/>
                                </span>
                                <span className={`${Styles.statsFree}`}>
                                    {t('свободно')} {storageSizeHandler(storage.free)}
                                </span>
                            </div>
                            <div className={`${Styles.statsDivider}`}/>
                            <div className={`${Styles.statsItem} ${Styles.statsItemEnd}`}>
                                <span className={`${Styles.statsName}`}>{t('К оплате')}</span>
                                <span className={`${Styles.statsValueAccent}`}>
                                    {amountHandler(step === 'select' ? payPrice : purchase?.amount)} {step === 'select' ? currency : (purchase?.currency || currency)}
                                </span>
                            </div>
                        </div>

                        {step === 'select' ? (
                            <>
                                {errorText ? (
                                    <div className={`${Styles.warnRow}`}>
                                        <span className={`${Styles.warnIcon}`}>!</span>
                                        <span>{errorText}</span>
                                    </div>
                                ) : null}

                                <div className={`${Styles.packsLabel}`}>{t('Выберите пакет')}</div>

                                {packages.length ? (
                                    <div className={`${Styles.packsWrap}`}>
                                        {packages.map((item)=>{
                                            const active = item.id === packId;

                                            return (
                                                <div
                                                    key={'storage-pack_' + item.id}
                                                    className={`${Styles.packItem} ${active ? Styles.packItemActive : ''}`}
                                                    onClick={()=>{setPackId(item.id)}}
                                                >
                                                    {active ? <span className={`${Styles.packMark}`}>✓</span> : null}
                                                    <span className={`${Styles.packValue}`}>{item.gb} GB</span>
                                                    <span className={`${Styles.packName}`}>
                                                        {t('на')} {item.months} {monthsName(item.months)}
                                                    </span>
                                                    <span className={`${Styles.packDivider}`}/>
                                                    <span className={`${Styles.packPrice}`}>
                                                        {amountHandler(item.price)} {currency}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className={`${Styles.emptyNote}`}>{t('Пакеты пока не настроены')}</div>
                                )}

                                {plans.length ? (
                                    <div className={`${Styles.plansWrap}`}>
                                        <div className={`${Styles.packsLabel}`}>{t('Уже куплено')}</div>
                                        {plans.map((item)=>(
                                            <div className={`${Styles.planRow}`} key={'storage-plan_' + item.storage_purchase_id}>
                                                <span>{item.gb} GB</span>
                                                <span className={`${Styles.planValue}`}>
                                                    {t('до')} {noteDateHandler(item.ends_at)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </>
                        ) : null}

                        {step === 'payment' ? (
                            <>
                                <div className={`${Styles.payRow}`}>
                                    <span className={`${Styles.payName}`}>{t('Будет добавлено')}</span>
                                    <span className={`${Styles.payValue}`}>
                                        {purchase?.gb} GB · {purchase?.months} {monthsName(purchase?.months)}
                                    </span>
                                </div>

                                {payAddress ? (
                                    <div className={`${Styles.qrWrap}`}>
                                        <div className={`${Styles.qrBox}`}>
                                            <QRCodeCanvas value={payAddress} size={150} includeMargin/>
                                        </div>
                                        <div className={`${Styles.qrNote}`}>
                                            {t('Отсканируйте в кошельке или скопируйте адрес')}
                                        </div>
                                    </div>
                                ) : null}

                                {primaryOption ? (
                                    <div className={`${Styles.payBox}`}>
                                        <div className={`${Styles.payBoxRow}`}>
                                            <span className={`${Styles.payBoxName}`}>{t('Монета')}</span>
                                            <span className={`${Styles.payBoxValue}`}>
                                                {primaryOption.currency?.code}
                                                {primaryOption.currency?.chain ? ` · ${primaryOption.currency.chain}` : ''}
                                            </span>
                                        </div>
                                        <div className={`${Styles.payBoxRow}`}>
                                            <span className={`${Styles.payBoxName}`}>{t('Сумма')}</span>
                                            <span className={`${Styles.payBoxValue}`}>
                                                {tokenAmountHandler(primaryOption.amount, primaryOption.currency?.decimals)}
                                            </span>
                                        </div>
                                        <div className={`${Styles.payBoxRow}`}>
                                            <span className={`${Styles.payBoxName}`}>{t('Адрес')}</span>
                                            <span
                                                className={`${Styles.payBoxAddress}`}
                                                onClick={()=>{copyHandler(primaryOption.wallet?.address)}}
                                            >
                                                {primaryOption.wallet?.address}
                                            </span>
                                        </div>
                                        <div className={`${Styles.payBoxHint}`}>
                                            {copied ? t('Адрес скопирован') : t('Нажмите на адрес, чтобы скопировать')}
                                        </div>
                                    </div>
                                ) : null}

                                {!primaryOption && purchase?.checkout_url ? (
                                    <a
                                        className={`${Styles.payLink}`}
                                        href={purchase.checkout_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {t('Перейти к оплате')}
                                    </a>
                                ) : null}

                                <div className={`${Styles.payStatus}`}>
                                    <span className={`${Styles.payStatusDot}`}/>
                                    <span>
                                        {t('Ждём оплату')}
                                        {secondsLeft ? ` — ${timerHandler(secondsLeft)}` : ''}
                                    </span>
                                </div>

                                {checkState === 'none' ? (
                                    <div className={`${Styles.checkNote}`}>
                                        {t('Платёж пока не поступил — попробуйте через пару минут')}
                                    </div>
                                ) : null}
                                {checkState === 'fail' ? (
                                    <div className={`${Styles.checkNote}`}>
                                        {t('Не удалось проверить оплату — попробуйте ещё раз')}
                                    </div>
                                ) : null}

                                <div className={`${Styles.rateNote}`}>
                                    {t('Окно можно закрыть: место добавится, как только платёж подтвердится')}
                                </div>
                            </>
                        ) : null}

                        {step === 'done' ? (
                            <>
                                <div className={`${Styles.doneWrap}`}>
                                    <span className={`${Styles.doneMark}`}>✓</span>
                                    <span className={`${Styles.doneTitle}`}>{t('Место добавлено')}</span>
                                </div>

                                <div className={`${Styles.summaryWrap}`}>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Добавлено')}</span>
                                        <span className={`${Styles.summaryValue}`}>
                                            +{grantedRef.current?.gb} GB
                                        </span>
                                    </div>
                                    <div className={`${Styles.summaryDivider}`}/>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Действует до')}</span>
                                        <span className={`${Styles.summaryValuePlain}`}>
                                            {noteDateHandler(grantedRef.current?.ends_at)}
                                        </span>
                                    </div>
                                    <div className={`${Styles.summaryDivider}`}/>
                                    <div className={`${Styles.summaryRow}`}>
                                        <span>{t('Хранилище теперь')}</span>
                                        <span className={`${Styles.summaryValuePlain}`}>
                                            {storageSizeHandler(storage.quota)}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </div>

                    <div className={`${Styles.modalFooter}`}>
                        <div
                            className={`${Styles.modalBtnGhost}`}
                            onClick={step === 'payment' ? cancelHandler : closeModal}
                        >
                            {step === 'payment' ? t('Отменить счёт') : step === 'done' ? t('Закрыть') : t('Отмена')}
                        </div>

                        {step === 'select' ? (
                            <div
                                className={`${Styles.modalBtn} ${selectedPack ? '' : Styles.modalBtnOff}`}
                                onClick={payHandler}
                            >
                                {purchaseStarted
                                    ? t('Создаём счёт…')
                                    : `${t('Оплатить')} ${amountHandler(payPrice)} ${currency}`}
                            </div>
                        ) : null}

                        {step === 'payment' ? (
                            <div
                                className={`${Styles.modalBtn} ${checkState === 'checking' ? Styles.modalBtnOff : ''}`}
                                onClick={checkPaymentHandler}
                            >
                                {checkState === 'checking' ? t('Проверяем…') : t('Проверить оплату')}
                            </div>
                        ) : null}

                        {step === 'done' ? (
                            <div className={`${Styles.modalBtn}`} onClick={closeModal}>
                                {t('Продолжить')}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetCapsuleStorageModal;
